import classNames from 'classnames';
import { Dispatch, SetStateAction } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { useAppContext } from '~/App';
import { TaskInput } from '~/generated/graphql';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { Asset, AssetSelect, useAssets } from '../AssetSelect/AssetSelect';
import {
  AttributeSelect,
  AttributeSelect_Query,
} from '../form/AttributeSelect';
import { Editor } from '../form/editor/Editor';
import { Input } from '../form/TextField';
import {
  AssetDrivenBadge,
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from '../job/AssetDrivenTemplateIndicator';
import { ManageTasks, UseTemplate } from '../job/CreateJobForm/ManageTasks';
import { TagCombobox, TagEntityType } from '../TagCombobox';
import { Tag } from '../TagCombobox/tags';
import { Accordion } from '../ui/Accordion';
import { Label } from '../ui/Label';
import { Switch } from '../ui/nucleus/Switch';
import { ControlledUpload } from '../upload2/Upload';

export type JobWhatSectionProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  register: UseFormRegister<T>;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  errors: FieldErrors<T>;

  // Context determines behavior and labels
  context: 'job' | 'template' | 'automation';
  isMobile?: boolean;

  // Data
  jobTemplates?: readonly JobTemplateFieldsFragment[];
  allTags?: Tag[];
  guestyEnabled?: boolean;

  // Task management
  taskState: [TaskInput[], Dispatch<SetStateAction<TaskInput[]>>];
  onShowTask: (id: string) => void;
  onTaskTypeUpdate?: (taskId: string, taskType: string) => void;
  /** Function to determine if a task's type can be edited */
  isTaskTypeEditable?: (task: TaskInput) => boolean;

  // Optional field visibility overrides
  showJobName?: boolean; // automation has separate name field
  showEnforceOrder?: boolean; // jobs need this, templates/automations don't
  showTemplateSelector?: boolean; // only for jobs
  animateFieldId?: string | null;

  // Template management (for jobs that use templates)
  onUseTemplateSelect?: (template: JobTemplateFieldsFragment) => void;
  selectedTemplate?: JobTemplateFieldsFragment | null;

  // Asset-driven template props
  /** Whether an asset-driven template is currently selected */
  isAssetDrivenTemplate?: boolean;
  /** Selected asset IDs for asset-driven templates */
  selectedAssetIds?: string[];
  /** Callback when asset selection changes */
  onAssetSelectionChange?: (assetIds: string[]) => void;
  /** Scope IDs to filter assets by location */
  scopeIds?: string[];
  /** Template behavior type - determines if we show "Task Rules" vs "Tasks" */
  templateBehavior?: 'static' | 'asset_rules';
};

/**
 * JobWhatSection - Shared "What" configuration across Job, Template, and Automation forms
 *
 * This component provides a unified interface for configuring (in order):
 * 1. Template selector (jobs only, when no tasks exist)
 * 2. Summary section: Job/Template name, Notes, Attachments, Guesty integration
 * 3. Location attributes (site attributes to display)
 * 4. Custom fields (job attributes to collect)
 * 5. Tags
 * 6. Tasks section with optional enforce order toggle
 *
 * Used by:
 * - JobTemplateForm (context='template') — currently the only caller
 *
 * CreateJobForm and AutomationForm still use inline What sections.
 * See MIGRATION.md in this folder.
 */
export function JobWhatSection<T extends FieldValues = FieldValues>({
  control,
  register,
  errors,
  context,
  isMobile,
  jobTemplates,
  allTags,
  guestyEnabled,
  taskState,
  onShowTask,
  onTaskTypeUpdate,
  isTaskTypeEditable,
  showJobName = true,
  showEnforceOrder = true,
  showTemplateSelector = true,
  animateFieldId,
  onUseTemplateSelect,
  selectedTemplate,
  isAssetDrivenTemplate: isAssetDrivenProp = false,
  selectedAssetIds = [],
  onAssetSelectionChange,
  scopeIds = [],
  templateBehavior,
}: JobWhatSectionProps<T>) {
  const { t } = useTranslation();
  const { ability } = useAppContext();

  const [tasks] = taskState;
  const activeTasks = tasks.filter((task) => !task._destroy);

  // Load assets for asset-driven template selection
  const [allAssets] = useAssets();

  // Check if selected template is asset-driven
  // Use prop if provided (for template form), otherwise compute from selectedTemplate
  const computedTemplateBehavior = getTemplateBehavior(selectedTemplate);
  const currentTemplateBehavior = templateBehavior ?? computedTemplateBehavior;
  const showAssetSelection =
    isAssetDrivenProp || isAssetDrivenTemplate(currentTemplateBehavior);

  // Get selected assets from IDs
  const selectedAssets = allAssets.filter((asset) =>
    selectedAssetIds.includes(asset.id)
  );

  // Handle asset selection change
  const handleAssetChange = (assets: Asset[]) => {
    if (onAssetSelectionChange) {
      onAssetSelectionChange(assets.map((a) => a.id));
    }
  };

  // Check if site attributes exist
  const [siteAttributesResult] = useQuery({
    query: AttributeSelect_Query,
    variables: { entityType: 'Site' },
    requestPolicy: 'cache-first',
  });

  // Check if job attributes exist
  const [jobAttributesResult] = useQuery({
    query: AttributeSelect_Query,
    variables: { entityType: 'Job' },
    requestPolicy: 'cache-first',
  });

  const hasSiteAttributes =
    !siteAttributesResult.fetching &&
    (siteAttributesResult.data?.attributes?.length ?? 0) > 0;

  const hasJobAttributes =
    !jobAttributesResult.fetching &&
    (jobAttributesResult.data?.attributes?.length ?? 0) > 0;

  // Determine name field label based on context
  const nameLabel =
    context === 'template'
      ? t('templateName')
      : context === 'automation'
      ? 'Job Name'
      : t('form.name');

  const nameMaxLength = context === 'template' ? 32 : 250;

  return (
    <>
      {/* Template Selector (jobs only, when no tasks or asset-driven template) */}
      {context === 'job' &&
        showTemplateSelector &&
        (activeTasks.length === 0 || showAssetSelection) &&
        jobTemplates &&
        jobTemplates.length > 0 && (
          <Accordion.PanelSection heading={t('translation:template')}>
            <UseTemplate
              jobTemplates={jobTemplates}
              taskState={taskState}
              onUseTemplateSelect={onUseTemplateSelect}
            />
            {/* Show badge when asset-driven template is selected */}
            {showAssetSelection && selectedTemplate && <AssetDrivenBadge />}
          </Accordion.PanelSection>
        )}

      {/* Asset Selection for Asset-Driven Templates */}
      {showAssetSelection && context === 'job' && (
        <Accordion.PanelSection>
          <div className='pb-1 text-base font-semibold text-primary'>
            {t('job:assetDrivenTemplate.selectAssets', 'Select Assets')}
          </div>
          <p className='pb-3 text-sm text-secondary'>
            {t(
              'job:assetDrivenTemplate.selectAssetsDescription',
              'Tasks will be created for each selected asset that matches the template rules. Only assets matching the template criteria will receive tasks.'
            )}
          </p>
          <AssetSelect
            assets={allAssets}
            value={selectedAssets}
            onChange={handleAssetChange}
            multiple={true}
            enableFilters={true}
            defaultLocationId={scopeIds[0]}
          />
          {selectedAssetIds.length > 0 && (
            <div className='mt-2 text-sm text-secondary'>
              {selectedAssetIds.length}{' '}
              {selectedAssetIds.length === 1 ? 'asset' : 'assets'} selected
            </div>
          )}
        </Accordion.PanelSection>
      )}

      {/* Summary Section: Name, Notes, Attachments, Guesty */}
      <Accordion.PanelSection heading='Summary'>
        {showJobName && (
          <Input
            {...register('name' as Path<T>)}
            label={nameLabel}
            required
            error={
              typeof errors.name?.message === 'string'
                ? errors.name.message
                : false
            }
            autoFocus={!isMobile && context !== 'automation'}
            maxLength={nameMaxLength}
            className={classNames('mb-4', {
              'animate-shake': animateFieldId === 'name',
            })}
          />
        )}

        <div>
          <Label>{t('translation:notes')}</Label>
          <Controller
            name={'notes' as Path<T>}
            control={control}
            render={({ field }) => (
              <Editor
                content={field.value}
                onUpdate={field.onChange}
                placeholder='Add notes...'
              />
            )}
          />
        </div>

        <div className={classNames({ 'mb-2': !guestyEnabled })}>
          <Controller
            name={'attachments' as Path<T>}
            control={control}
            render={({ field: { value, onChange } }) => (
              <ControlledUpload
                id={`${context}Form`}
                value={value ?? []}
                onChange={onChange}
                isPill={context === 'job'}
              />
            )}
          />
        </div>

        {guestyEnabled && (
          <div className='mb-5 block'>
            <label className='flex items-center gap-1.5 text-sm text-grey-50'>
              <input
                {...register('guestyCleaningStatus' as Path<T>)}
                type='checkbox'
                className='h-4 w-4'
              />
              Update Guesty Cleaning Status
            </label>
          </div>
        )}
      </Accordion.PanelSection>

      {/* Location Attributes */}
      {hasSiteAttributes && (
        <Accordion.PanelSection>
          <div className='pb-1 text-base font-semibold text-primary'>
            {t('translation:location_attributes')}
          </div>
          <p className='pb-3 text-sm text-secondary'>
            Information about the job location relevant to this job
          </p>
          <Controller
            name={'includeAttributes' as Path<T>}
            control={control}
            render={({ field: { value, onChange } }) => (
              <AttributeSelect
                entityType='Site'
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Accordion.PanelSection>
      )}

      {/* Custom Fields (Job Attributes) */}
      {ability.can('use', 'feat.job-attributes') && hasJobAttributes && (
        <Accordion.PanelSection>
          <div className='pb-1 text-base font-semibold text-primary'>
            Custom Fields
          </div>
          <p className='pb-3 text-sm text-secondary'>
            Metrics you would like collected for this job
          </p>
          <Controller
            name={'jobAttributes' as Path<T>}
            control={control}
            render={({ field: { value, onChange } }) => (
              <AttributeSelect
                entityType='Job'
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Accordion.PanelSection>
      )}

      {/* Tags */}
      <Accordion.PanelSection heading={t('translation:tag_plural')}>
        <Controller
          name={'tags' as Path<T>}
          control={control}
          render={({ field: { value, onChange } }) => (
            <TagCombobox
              entityType={TagEntityType.Job}
              options={allTags || []}
              value={allTags?.filter((t) => value?.includes(t.id)) || []}
              onChange={(tags) => {
                onChange(tags.length ? tags.map(({ id }) => id) : null);
              }}
              hasLabel={false}
            />
          )}
        />
      </Accordion.PanelSection>

      {/* Tasks/Rules Section */}
      <Accordion.PanelSection
        heading={
          currentTemplateBehavior === 'asset_rules'
            ? t('jobTemplate.rules.heading', 'Task Rules')
            : t('jobTemplate.tasks.heading', 'Tasks')
        }
      >
        <div className='mb-5'>
          <ManageTasks
            state={taskState}
            onShowTask={onShowTask}
            onTaskTypeUpdate={onTaskTypeUpdate || (() => {})}
            isTaskEditable={isTaskTypeEditable}
            label={
              currentTemplateBehavior === 'asset_rules'
                ? t('jobTemplate.rules.label', 'Task Rules')
                : t('jobTemplate.tasks.label', 'Tasks')
            }
            placeholder={
              currentTemplateBehavior === 'asset_rules'
                ? t('jobTemplate.rules.placeholder', 'Task Rule Name')
                : t('jobTemplate.tasks.placeholder', 'Task Name')
            }
            allowedTaskTypes={
              currentTemplateBehavior === 'asset_rules' ? ['Asset'] : undefined
            }
            defaultTaskType={
              currentTemplateBehavior === 'asset_rules' ? 'Asset' : undefined
            }
            templateBehavior={currentTemplateBehavior}
          />

          {showEnforceOrder && activeTasks.length > 0 && (
            <Controller
              name={'enforceOrder' as Path<T>}
              control={control}
              render={({ field }) => {
                const isRules = currentTemplateBehavior === 'asset_rules';
                return (
                  <Switch
                    label={
                      isRules
                        ? t(
                            'jobTemplate.rules.enforceOrder',
                            'Enforce rule order'
                          )
                        : t(
                            'jobTemplate.tasks.enforceOrder',
                            'Enforce task order'
                          )
                    }
                    tooltip={
                      isRules
                        ? t(
                            'jobTemplate.rules.enforceOrderTooltip',
                            'Each rule will need to be completed before the next rule can be started.'
                          )
                        : t(
                            'jobTemplate.tasks.enforceOrderTooltip',
                            'Each task will need to be completed before the next task can be started.'
                          )
                    }
                    checked={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                );
              }}
            />
          )}
        </div>
      </Accordion.PanelSection>
    </>
  );
}
